import { Component, OnInit, computed } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AuthFragmentService } from './core/services/auth-fragment.service';
import { AuthService } from './core/services/auth.service';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { NotificationService } from './core/services/notification.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NgFor, NgIf, NgClass],
  template: `
    <router-outlet></router-outlet>

    <div class="app-toast-container" *ngIf="notifications().length > 0">
      <div
        *ngFor="let msg of notifications()"
        class="app-toast"
        [ngClass]="{
          'app-toast--error': msg.type === 'error',
          'app-toast--success': msg.type === 'success',
          'app-toast--info': msg.type === 'info'
        }"
      >
        <div class="app-toast__icon" aria-hidden="true">
          <span *ngIf="msg.type === 'error'">!</span>
          <span *ngIf="msg.type === 'success'">✓</span>
          <span *ngIf="msg.type === 'info'">i</span>
        </div>
        <div class="app-toast__body">
          <span class="app-toast__title" *ngIf="msg.type === 'error' && !msg.hideTitle">Something went wrong</span>
          <span class="app-toast__title" *ngIf="msg.type === 'success'">Done</span>
          <span class="app-toast__title" *ngIf="msg.type === 'info'">Info</span>
          <span class="app-toast__text">{{ msg.text }}</span>
        </div>
        <button
          type="button"
          class="app-toast__close"
          (click)="dismiss(msg.id)"
          aria-label="Close notification"
        >
          ×
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-height: 100vh;
      background-color: #000000;
    }

    .app-toast-container {
      position: fixed;
      top: 20px;
      right: 20px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      z-index: 1000;
      pointer-events: none;
    }

    .app-toast {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      max-width: 420px;
      padding: 12px 16px;
      border-radius: 12px;
      background-color: #111827;
      color: #f9fafb;
      box-shadow: 0 18px 40px rgba(0, 0, 0, 0.65);
      font-size: 14px;
      pointer-events: auto;
      border: 1px solid rgba(249, 250, 251, 0.06);
      animation: app-toast-fade-in 160ms ease-out;
    }

    .app-toast--error {
      border-color: rgba(248, 113, 113, 0.5);
    }

    .app-toast--success {
      border-color: rgba(52, 211, 153, 0.5);
    }

    .app-toast--info {
      border-color: rgba(129, 140, 248, 0.5);
    }

    .app-toast__icon {
      width: 28px;
      height: 28px;
      border-radius: 999px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: 600;
    }

    .app-toast--error .app-toast__icon {
      background: rgba(248, 113, 113, 0.1);
      color: #fecaca;
    }

    .app-toast--success .app-toast__icon {
      background: rgba(52, 211, 153, 0.1);
      color: #bbf7d0;
    }

    .app-toast--info .app-toast__icon {
      background: rgba(129, 140, 248, 0.1);
      color: #c7d2fe;
    }

    .app-toast__body {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .app-toast__title {
      font-weight: 600;
      font-size: 13px;
      opacity: 0.85;
    }

    .app-toast__text {
      line-height: 1.5;
      font-size: 13px;
      opacity: 0.9;
    }

    .app-toast__close {
      background: transparent;
      border: none;
      color: rgba(249, 250, 251, 0.7);
      cursor: pointer;
      font-size: 16px;
      line-height: 1;
      padding: 0 4px;
    }

    .app-toast__close:hover {
      color: #ffffff;
    }

    @keyframes app-toast-fade-in {
      from {
        opacity: 0;
        transform: translateY(-6px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `]
})
export class AppComponent implements OnInit {
  title = 'Feather Subscriptions';
  readonly notifications = computed(() => this.notificationService.messages());

  constructor(
    private readonly authFragmentService: AuthFragmentService,
    private readonly authService: AuthService,
    private readonly notificationService: NotificationService
  ) {}

  dismiss(id: number): void {
    this.notificationService.dismiss(id);
  }

  async ngOnInit(): Promise<void> {
    const payload = await this.authFragmentService.tryDecryptFragment();
    if (payload) {
      this.authService.setCredentials(payload.shopId, payload.userId, payload.token);
    }
  }
}
